import { useCallback, useRef, useState } from "react";

import { BdsIconUploadSimple, BdsText } from "@buildertrend/components";

import "./InboxDropzoneWrapper.less";

interface IInboxDropzoneWrapperProps {
    isActive: boolean;
    onFilesDropped: (files: File[]) => void;
    children: React.ReactNode;
}

export const InboxDropzoneWrapper: React.FC<IInboxDropzoneWrapperProps> = ({
    isActive,
    onFilesDropped,
    children,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [overlayTopOffset, setOverlayTopOffset] = useState(0);
    const dragCounterRef = useRef(0);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const handleDragEnter = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isActive) return;
            dragCounterRef.current++;
            if (dragCounterRef.current === 1 && e.dataTransfer.types.includes("Files")) {
                if (wrapperRef.current) {
                    const anchor =
                        wrapperRef.current.querySelector<HTMLElement>("[data-dropzone-anchor]");
                    if (anchor) {
                        const wrapperTop = wrapperRef.current.getBoundingClientRect().top;
                        const anchorTop = anchor.getBoundingClientRect().top;
                        setOverlayTopOffset(anchorTop - wrapperTop);
                    }
                }
                setIsDragging(true);
            }
        },
        [isActive],
    );

    const handleDragLeave = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isActive) return;
            dragCounterRef.current--;
            if (dragCounterRef.current === 0) {
                setIsDragging(false);
            }
        },
        [isActive],
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            if (e.defaultPrevented) return;
            e.preventDefault();
            e.stopPropagation();
            if (!isActive) return;
            dragCounterRef.current = 0;
            setIsDragging(false);
            const files = Array.from(e.dataTransfer.files);
            if (files.length > 0) {
                onFilesDropped(files);
            }
        },
        [isActive, onFilesDropped],
    );

    if (!isActive) {
        return <>{children}</>;
    }

    return (
        <div
            ref={wrapperRef}
            className="InboxDropzoneWrapper"
            data-testid="inboxDropzoneWrapper"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {children}
            {isDragging && (
                <div
                    className="InboxDropzoneWrapper-overlay"
                    data-testid="inboxDropzoneOverlay"
                    style={{ top: overlayTopOffset }}
                >
                    <div className="InboxDropzoneWrapper-overlayContent">
                        <div className="InboxDropzoneWrapper-overlayIcon">
                            <BdsIconUploadSimple size="3xl" />
                        </div>
                        <BdsText
                            text="Drop to create draft bills"
                            size="distinct-lg"
                            className="InboxDropzoneWrapper-overlayText"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
